import { useState } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Plus } from 'lucide-react';
import { Position } from '@/types/portfolio';
import { useToast } from '@/hooks/use-toast';

interface AddPositionDialogProps {
  onAddPosition: (position: Omit<Position, 'id'>) => void;
}

export function AddPositionDialog({ onAddPosition }: AddPositionDialogProps) {
  const [open, setOpen] = useState(false);
  const [ticker, setTicker] = useState('');
  const [buyPrice, setBuyPrice] = useState('');
  const [quantity, setQuantity] = useState('');
  const { toast } = useToast();
  
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    const tickerUpper = ticker.toUpperCase().trim();
    const priceNum = parseFloat(buyPrice);
    const qtyNum = parseInt(quantity);
    
    if (!tickerUpper || tickerUpper.length < 1 || tickerUpper.length > 10) {
      toast({
        title: "Invalid Ticker",
        description: "Please enter a valid stock ticker (1-10 characters)",
        variant: "destructive"
      });
      return;
    }
    
    if (isNaN(priceNum) || priceNum <= 0 || priceNum > 1000000) {
      toast({
        title: "Invalid Price",
        description: "Please enter a valid buy price (0-1,000,000)",
        variant: "destructive"
      });
      return;
    }
    
    if (isNaN(qtyNum) || qtyNum <= 0 || qtyNum > 1000000) {
      toast({
        title: "Invalid Quantity",
        description: "Please enter a valid quantity (1-1,000,000)",
        variant: "destructive"
      });
      return;
    }
    
    onAddPosition({
      ticker: tickerUpper,
      buyPrice: priceNum,
      quantity: qtyNum
    });
    
    setTicker('');
    setBuyPrice('');
    setQuantity('');
    setOpen(false);
    
    toast({
      title: "Position Added",
      description: `${tickerUpper} position added successfully`,
    });
  };
  
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size="lg" className="shadow-lg">
          <Plus className="mr-2 h-5 w-5" />
          Add Position
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Add Stock Position</DialogTitle>
          <DialogDescription>
            Enter the details of your stock position to track its performance
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4 mt-4">
          <div className="space-y-2">
            <Label htmlFor="ticker">Stock Ticker</Label>
            <Input
              id="ticker"
              placeholder="AAPL, MSFT, etc."
              value={ticker}
              onChange={(e) => setTicker(e.target.value)}
              maxLength={10}
              required
            />
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="buyPrice">Buy Price ($)</Label>
            <Input
              id="buyPrice"
              type="number"
              step="0.01"
              min="0.01"
              max="1000000"
              placeholder="150.00"
              value={buyPrice}
              onChange={(e) => setBuyPrice(e.target.value)}
              required
            />
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="quantity">Quantity</Label>
            <Input
              id="quantity"
              type="number"
              step="1"
              min="1"
              max="1000000"
              placeholder="100"
              value={quantity}
              onChange={(e) => setQuantity(e.target.value)}
              required
            />
          </div>
          
          <div className="flex justify-end gap-3 pt-4">
            <Button type="button" variant="outline" onClick={() => setOpen(false)}>
              Cancel
            </Button>
            <Button type="submit">Add Position</Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
